'use client';

import { Highlight, themes } from 'prism-react-renderer';
import React, { useEffect, useState } from 'react';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { components } from '@/types/strapi';

type ProgrammingLanguage =
	components['schemas']['ItemsCodeBlockComponent']['language'];

const languageTabs: { id: ProgrammingLanguage; label: string }[] = [
	{ id: 'bash', label: 'Curl' },
	{ id: 'java', label: 'Java' },
	{ id: 'python', label: 'Python' },
	{ id: 'javascript', label: 'JavaScript' },
	{ id: 'php', label: 'PHP' },
	{ id: 'csharp', label: '.NET' },
];

function getDefaultLanguage(
	codeBlocks: NonNullable<
		components['schemas']['DynamicZoneApiDocumentationComponent']['code_blocks']
	>,
): ProgrammingLanguage | null {
	const availableLanguages = languageTabs
		.filter(tab => codeBlocks.some(block => block.language === tab.id))
		.map(tab => tab.id);

	return availableLanguages[0] ?? null;
}

export const ApiDocumentation = ({
	heading,
	sub_heading,
	code_blocks,
	isFirst = false,
}: components['schemas']['DynamicZoneApiDocumentationComponent'] & {
	isFirst?: boolean;
}) => {
	const SectionHeading = sectionHeadingTag(isFirst);
	const [isClient, setIsClient] = useState(false);
	const [selectedLanguage, setSelectedLanguage] =
		useState<ProgrammingLanguage | null>(() =>
			code_blocks?.length ? getDefaultLanguage(code_blocks) : null,
		);

	useEffect(() => {
		setIsClient(true);

		if (code_blocks?.length) {
			const defaultLanguage = getDefaultLanguage(code_blocks);
			if (defaultLanguage) {
				setSelectedLanguage(prev =>
					prev && code_blocks.some(block => block.language === prev)
						? prev
						: defaultLanguage,
				);
			}
		}
	}, [code_blocks]);

	if (!code_blocks || code_blocks.length === 0) {
		console.warn('No code blocks provided');
		return null;
	}

	// Get language for syntax highlighter
	const getLanguage = (lang: ProgrammingLanguage): string => {
		if (!lang) return 'text';
		return lang === 'csharp' ? 'csharp' : lang;
	};

	// Get code blocks for the selected language
	const getLanguageCodeBlocks = (
		language: ProgrammingLanguage,
	): { title: string; code: string }[] => {
		return code_blocks
			.filter(block => block.language === language)
			.map(block => ({
				title: block.title || '',
				code: block.code_snippet || '',
			}));
	};

	// Check if a language has any implementations
	const hasLanguageImplementation = (
		language: ProgrammingLanguage,
	): boolean => {
		return code_blocks.some(block => block.language === language);
	};

	// If no language is selected yet, don't render the code blocks
	if (!selectedLanguage) {
		return null;
	}

	return (
		<section className="api-documentation-section w-full bg-white py-8">
			<noscript>
				<style>{`
					.api-documentation-section pre,
					.api-documentation-section code {
						opacity: 1 !important;
						visibility: visible !important;
					}
				`}</style>
			</noscript>
			<div className="container mx-auto px-4 md:px-6">
				{/* Heading Section */}
				{(heading || sub_heading) && (
					<div className="mx-auto mb-8 text-center">
						{heading && (
							<SectionHeading className="mb-2 text-2xl font-bold text-gray-900">
								{heading}
							</SectionHeading>
						)}
						{sub_heading && (
							<p className="text-md text-gray-600">
								{sub_heading}
							</p>
						)}
					</div>
				)}

				{/* Single Code Block with Language Tabs */}
				<div className="mx-auto max-w-4xl">
					<div className="rounded-lg border border-gray-200 bg-gray-50 shadow-lg">
						{/* Language Selection Tabs */}
						<div className="border-b border-gray-200">
							<div className="flex items-center overflow-x-auto bg-gray-700 px-4">
								{languageTabs
									.filter(tab =>
										hasLanguageImplementation(tab.id),
									)
									.map(tab => (
										<button
											key={tab.id}
											onClick={() =>
												setSelectedLanguage(tab.id)
											}
											className={`min-w-fit whitespace-nowrap px-6 py-3 text-sm font-medium transition-colors ${
												selectedLanguage === tab.id
													? 'border-b-2 border-blue-600 bg-white text-blue-600'
													: 'text-white hover:bg-gray-300 hover:text-gray-800'
											}`}>
											{tab.label}
										</button>
									))}
							</div>
						</div>

						{/* Code Content */}
						<div className="divide-y divide-gray-200">
							{getLanguageCodeBlocks(selectedLanguage).map(
								(codeBlock, index) => (
									<div key={index} className="relative p-4">
										{codeBlock.title && (
											<h3 className="mb-3 text-sm font-medium text-gray-900">
												{codeBlock.title}
											</h3>
										)}
										{isClient ? (
											<Highlight
												theme={themes.github}
												code={codeBlock.code.trim()}
												language={getLanguage(
													selectedLanguage,
												)}>
												{({
													style,
													tokens,
													getLineProps,
													getTokenProps,
												}) => (
													<pre
														className="h-[400px] overflow-y-auto overflow-x-hidden rounded-md bg-gray-50 p-4 font-mono text-sm text-gray-800"
														style={style}>
														<code className="whitespace-pre-wrap break-all">
															{tokens.map(
																(line, i) => (
																	<div
																		key={i}
																		{...getLineProps(
																			{
																				line,
																			},
																		)}
																		className="flex">
																		<span className="flex-none select-none pr-4 text-right text-gray-400 opacity-50">
																			{i +
																				1}
																		</span>
																		<span className="flex-1 break-all">
																			{line.map(
																				(
																					token,
																					key,
																				) => (
																					<span
																						key={
																							key
																						}
																						{...getTokenProps(
																							{
																								token,
																							},
																						)}
																					/>
																				),
																			)}
																		</span>
																	</div>
																),
															)}
														</code>
													</pre>
												)}
											</Highlight>
										) : (
											<pre className="h-[400px] overflow-y-auto overflow-x-hidden rounded-md bg-gray-50 p-4 font-mono text-sm text-gray-800">
												<code className="block whitespace-pre-wrap break-all">
													{codeBlock.code.trim()}
												</code>
											</pre>
										)}

										{/* Copy button */}
										<button
											onClick={() =>
												navigator.clipboard.writeText(
													codeBlock.code.trim(),
												)
											}
											className="absolute right-3 top-3 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-100 hover:text-gray-900">
											Copy
										</button>
									</div>
								),
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default ApiDocumentation;
